import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDepots } from "@/api/depots";
import {
  CreateTripFormSchema,
  createTripSchema,
  useCreateTrip,
} from "@/api/trips";

export function CreateTripForm({ onSuccess }: { onSuccess?: () => void } = {}) {
  const form = useForm<CreateTripFormSchema>({
    resolver: zodResolver(createTripSchema),
    defaultValues: {
      depotId: "",
      color: "#3B82F6", // Default blue color
    },
  });

  // Fetch depots for the depot selection
  const { data: depots, isLoading } = useDepots();

  const createTripMutation = useCreateTrip({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    createTripMutation.mutate(values);
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8">
        {Object.keys(form.formState.errors).length > 0 && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">
              Please fix the following errors:
              <ul className="mt-2 list-disc pl-5">
                {Object.entries(form.formState.errors).map(([key, error]) => (
                  <li key={key}>
                    {error?.message?.toString() ?? `Invalid ${key}`}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="depotId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Depot</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a depot" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoading ? (
                    <SelectItem value="loading">Loading depots...</SelectItem>
                  ) : depots && depots.length > 0 ? (
                    depots.map((depot) => (
                      <SelectItem key={depot.id} value={depot.id}>
                        {depot.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-depots">
                      No depots available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trip Color</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input
                    type="color"
                    {...field}
                    className="h-10 w-20 cursor-pointer"
                  />
                </FormControl>
                <Input
                  type="text"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  placeholder="#RRGGBB"
                  className="flex-1"
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={createTripMutation.isPending}
          className="w-full"
        >
          {createTripMutation.isPending ? "Creating..." : "Create Trip"}
        </Button>
      </form>
    </Form>
  );
}
